import { getStatusNumber } from '../../presentation/utils/statusChatMap';
import { sidebarChannelMatchesConversationType } from './inboxMsChatPlatform';
import { getTemperatureLevelFromScore } from '../../presentation/modules/inbox/utils/leadScoreUtils';

function extractConversationTagIds(tags) {
  if (!tags) return [];

  if (Array.isArray(tags)) {
    return tags
      .map((tag) => (tag && typeof tag === 'object' ? tag.id : tag))
      .filter((tagId) => tagId !== null && tagId !== undefined)
      .map((tagId) => String(tagId));
  }

  if (typeof tags === 'object' && tags.id !== null && tags.id !== undefined) {
    return [String(tags.id)];
  }

  return [String(tags)];
}

/**
 * Normaliza filtros avanzados de etiquetas (mismo formato que multi-select-group en inbox):
 * array de grupos donde cada grupo es un array de tags o ids; si viene plano, un solo grupo.
 * Alineado con TagsInFilter / TagsOutFilter (AND dentro del grupo, OR entre grupos).
 */
function normalizeAdvancedFilterTagGroups(tagsFilter) {
  if (!tagsFilter || !Array.isArray(tagsFilter) || tagsFilter.length === 0) {
    return [];
  }

  const isGroupStructure = tagsFilter.every((item) => Array.isArray(item));
  const groups = isGroupStructure
    ? tagsFilter.map((group) => extractConversationTagIds(group))
    : [extractConversationTagIds(tagsFilter)];

  return groups.filter((ids) => ids.length > 0);
}

/**
 * @fileoverview Servicio para evaluación de filtros de conversación en Inbox.
 * @module conversationFilterEvaluator
 * @category Service - Inbox
 * @since 2.0.0
 * @version 3.1.0
 *
 * @typedef {Object} Conversation
 * @description Objeto de conversación con atributos y canal.
 * @property {string} type - Canal de la conversación (ej: 'whatsapp', 'messenger').
 * @property {Object} attributes - Atributos adicionales de la conversación.
 * @property {Object} [attributes.assigned_agent] - Agente asignado.
 * @property {string|number} [attributes.assigned_agent.id] - ID del agente asignado.
 * @property {boolean} [attributes.mia_chatbot_active] - Si el chatbot MIA está activo.
 * @property {boolean} [attributes.buy_intention] - Si hay intención de compra.
 * @property {boolean} [attributes.human_help] - Si requiere ayuda humana.
 *
 * @typedef {Object} Filter
 * @description Filtros aplicados a la conversación.
 * @property {string} [channel] - Canal a filtrar.
 * @property {string|number} [platformRetailerId] - Cuenta/línea concreta a filtrar.
 * @property {string} [msPlatform] - Subtipo del MS de la cuenta seleccionada (ej. whatsapp_qr_messages).
 * @property {'mia_active'|'buy_intention'|'human_help'} [mia] - Filtro MIA.
 * @property {'inbox'|'not_assigned'|'all'} [conversation] - Filtro de asignación.
 * @property {string} [searchTerm] - Término de búsqueda libre.
 * @property {{ activity?: string[], status?: string[] }} [quickFilters] - Filtros rápidos de actividad y estado.
 *
 * @function conversationMatchesFilter
 * @category Service - Inbox
 * @description
 * Evalúa si una conversación cumple con los filtros especificados para la bandeja de entrada.
 *
 * **Flujo de Operación:**
 * 1. Valida canal de la conversación.
 * 2. Valida atributos MIA (chatbot, intención de compra, ayuda humana).
 * 3. Valida asignación de agente según filtro de conversación.
 * 4. Retorna true si cumple todos los filtros, false en caso contrario.
 *
 * **Validaciones:**
 * - Canal
 * - Estado MIA
 * - Asignación de agente
 *
 * @param {Conversation} conversation - Conversación a evaluar.
 * @param {Filter} filter - Filtros aplicados.
 * @param {string|number} currentUserId - ID del usuario actual para validación de asignación.
 * @param {Object} [options] - Opciones adicionales para validación.
 * @param {Object} [options.currentRetailerUser] - Usuario actual con permisos de visualización.
 * @returns {boolean} true si la conversación cumple los filtros, false si no.
 *
 * @example
 * // Ejemplo de uso
 * const cumple = conversationMatchesFilter(conversation, { channel: 'whatsapp', mia: 'mia_active', conversation: 'inbox' }, 'user_123');
 */

export function normalizeAgentId(value) {
  if (value == null || value === '') return null;
  return String(value);
}

/**
 * Reglas de visibilidad por asignación (alineadas con Notifications::AuthorizedUsers en backend).
 * No aplica filtros de UI del inbox (canal, MIA, no_read, etc.).
 */
export function canViewConversationByAssignment(currentRetailerUser, currentUserId, assignedAgentId) {
  if (!currentRetailerUser) return false;

  const normalizedAssignedAgentId = normalizeAgentId(assignedAgentId);
  const normalizedCurrentUserId = normalizeAgentId(currentUserId);
  const managedAgents = Array.isArray(currentRetailerUser.managed_agents)
    ? currentRetailerUser.managed_agents.map((agentId) => normalizeAgentId(agentId)).filter(Boolean)
    : [];

  if (normalizedAssignedAgentId) {
    const isOwnConversation = normalizedAssignedAgentId === normalizedCurrentUserId;
    const canViewAllManaged = currentRetailerUser.can_view_all_chats === true
      && (managedAgents.length === 0 || managedAgents.includes(normalizedAssignedAgentId));

    return isOwnConversation || canViewAllManaged;
  }

  return currentRetailerUser.can_view_available_chats === true
    || currentRetailerUser.can_view_all_chats === true;
}

export function extractAssignedAgentId(attributes = {}) {
  if (!attributes || typeof attributes !== 'object') return null;

  const candidates = [
    attributes.assigned_agent?.id,
    attributes.agent?.id,
    attributes.retailer_user_id,
    attributes.agent_id
  ];

  const matched = candidates.find((value) => value != null && value !== '');
  return matched ?? null;
}

const UNREAD_ONLY_CUSTOMER_UPDATE_KEYS = new Set([
  'has_unread_messages',
  'unread_messages_count',
  'unread_messages',
  'unread'
]);

/** Payload típico de marcar leído/no leído: solo flags, sin assigned_agent. */
export function isUnreadOnlyCustomerUpdate(attributes = {}) {
  const keys = Object.keys(attributes);
  if (keys.length === 0) return false;
  return keys.every((key) => UNREAD_ONLY_CUSTOMER_UPDATE_KEYS.has(key));
}

export function resolveAssignedAgentIdForUnreadCounter({
  eventAttributes,
  conversationsData,
  conversationIndexMap,
  chatId
}) {
  const fromEvent = extractAssignedAgentId(eventAttributes);
  if (fromEvent != null) return fromEvent;

  const index = conversationIndexMap?.[String(chatId)];
  if (index === undefined || !Array.isArray(conversationsData)) return null;

  return extractAssignedAgentId(conversationsData[index]?.attributes || {});
}

/**
 * Id de la cuenta/línea (`platform_retailer_id`) del chat.
 * Acepta el shape JSON:API (`attributes.platform_retailer_id`), el serializado de
 * la UI (`platformRetailerId`) y el chat plano (sin `attributes`).
 */
export function extractPlatformRetailerId(conversation) {
  if (!conversation) return null;

  const attrs = conversation.attributes || conversation;
  return attrs.platform_retailer_id
    ?? attrs.platformRetailerId
    ?? conversation.platformRetailerId
    ?? null;
}

export function conversationMatchesFilter(conversation, filter, currentUserId, options = {}) {
  if (!filter) return true;

  // 1. Validar canal (sidebar key vs MS enum / Rails-native platform)
  if (filter.channel) {
    const conversationChannel = conversation?.type || conversation?.attributes?.platform;
    if (!sidebarChannelMatchesConversationType(filter.channel, conversationChannel)) {
      return false;
    }
  }

  // 1.1 Validar la cuenta/línea concreta cuando el sidebar filtra por número.
  // Sin esto, los chats que llegan por socket desde otra línea se insertan en la
  // lista filtrada porque la evaluación solo miraba la familia de canal.
  const filterPlatformRetailerId = filter.platformRetailerId;
  if (filterPlatformRetailerId != null && filterPlatformRetailerId !== '') {
    const conversationPlatformRetailerId = extractPlatformRetailerId(conversation);
    if (conversationPlatformRetailerId == null
      || String(conversationPlatformRetailerId) !== String(filterPlatformRetailerId)) {
      return false;
    }
  }

  // 1.2 Si la cuenta seleccionada pertenece a un subtipo concreto del MS
  // (WhatsApp BSP/QR/Gupshup), el chat debe ser de ese subtipo.
  if (filter.msPlatform) {
    const conversationPlatform = conversation?.attributes?.platform || conversation?.type;
    if (conversationPlatform && conversationPlatform !== filter.msPlatform) {
      return false;
    }
  }

  // 2. Validar filtros MIA
  if (filter.mia) {
    switch (filter.mia) {
      case 'mia_active':
        if (!conversation?.attributes?.mia_chatbot_active) {
          return false;
        }
        break;

      case 'buy_intention':
        if (!conversation?.attributes?.buy_intention) {
          return false;
        }
        break;

      case 'human_help':
        if (!conversation?.attributes?.human_help) {
          return false;
        }
        break;

      default:
        break;
    }
  }

  // 3. Validar filtros rápidos (actividad y estado)
  const rawActivity = filter.quickFilters?.activity;
  const activityFilters = Array.isArray(rawActivity) ? rawActivity : [];
  const rawStatus = filter.quickFilters?.status;
  const statusFilters = Array.isArray(rawStatus) ? rawStatus : [];
  const effectiveStatusFilters = statusFilters.length > 0 ? statusFilters : ['pending'];
  const attrs = conversation?.attributes || {};

  // 3.1 Actividad: actualmente soporta `no_read` y `no_answer`
  if (activityFilters.length > 0) {
    let matchesActivity = false;

    if (activityFilters.includes('no_read')) {
      const hasUnreadFlag = attrs.has_unread_messages === true;
      const hasUnreadCount = typeof attrs.unread_messages_count === 'number'
        ? attrs.unread_messages_count > 0
        : false;

      if (hasUnreadFlag || hasUnreadCount) {
        matchesActivity = true;
      }
    }

    if (!matchesActivity && activityFilters.includes('no_answer')) {
      const recentInbound = attrs.recent_inbound_message_date;
      const lastInteraction = attrs.interaction_date || attrs.last_chat_interaction;

      if (recentInbound && lastInteraction) {
        const recentInboundDate = new Date(recentInbound);
        const lastInteractionDate = new Date(lastInteraction);

        if (!Number.isNaN(recentInboundDate.getTime()) && !Number.isNaN(lastInteractionDate.getTime())) {
          if (recentInboundDate >= lastInteractionDate) {
            matchesActivity = true;
          }
        }
      }
    }

    if (!matchesActivity) {
      return false;
    }
  }

  // 3.2 Estado: `pending` (0,1,2) o `resolved` (3)
  if (effectiveStatusFilters.length > 0) {
    const statusFilter = effectiveStatusFilters[0];
    let statusChat = attrs.status_chat;

    if (typeof statusChat !== 'number') {
      statusChat = getStatusNumber(statusChat);
    }

    if (statusFilter === 'all') {
      // No aplica restricción de estado.
    } else if (statusChat === null || Number.isNaN(statusChat)) {
      // Si no podemos resolver el estado, no debe pasar filtros explícitos de estado.
      return false;
    } else if (statusFilter === 'pending' && ![0, 1, 2].includes(statusChat)) {
      return false;
    } else if (statusFilter === 'resolved' && statusChat !== 3) {
      return false;
    }
  }

  // 4. Validar término de búsqueda simple sobre datos básicos del cliente
  const rawSearch = filter.searchTerm || '';
  const normalizedSearch = rawSearch.trim().toLowerCase();

  if (normalizedSearch.length > 0) {
    const fieldsToSearch = [];

    if (typeof attrs.first_name === 'string') fieldsToSearch.push(attrs.first_name);
    if (typeof attrs.last_name === 'string') fieldsToSearch.push(attrs.last_name);
    if (typeof attrs.whatsapp_name === 'string') fieldsToSearch.push(attrs.whatsapp_name);
    if (typeof attrs.username === 'string') fieldsToSearch.push(attrs.username);
    if (typeof attrs.email === 'string') fieldsToSearch.push(attrs.email);
    if (typeof attrs.phone === 'string') fieldsToSearch.push(attrs.phone);

    const fullName = [attrs.first_name, attrs.last_name]
      .filter(Boolean)
      .join(' ');

    if (fullName) {
      fieldsToSearch.push(fullName);
    }

    const matchesSearch = fieldsToSearch.some((value) => {
      if (typeof value !== 'string') return false;
      return value.toLowerCase().includes(normalizedSearch);
    });

    if (!matchesSearch) {
      return false;
    }
  }

  const currentRetailerUser = options?.currentRetailerUser || null;
  const normalizeId = normalizeAgentId;

  const assignedAgentId = attrs?.assigned_agent?.id ?? null;
  const normalizedAssignedAgentId = normalizeId(assignedAgentId);
  const normalizedCurrentUserId = normalizeId(currentUserId);

  // 5. Validar permisos de visualización por asignación cuando hay usuario disponible
  if (currentRetailerUser
    && !canViewConversationByAssignment(currentRetailerUser, currentUserId, normalizedAssignedAgentId)) {
    return false;
  }

  // 6. Validar asignación de agente según filtros avanzados
  if (filter.advancedFilters?.agent_id?.length > 0) {
    // Si no hay agente asignado, no cumple el filtro
    if (!assignedAgentId) {
      return false;
    }

    // Si el agente asignado no está en el filtro, no cumple el filtro
    const normalizedAgentIds = filter.advancedFilters?.agent_id
      ?.map((agentId) => normalizeId(agentId))
      .filter(Boolean) || [];

    if (!normalizedAgentIds.includes(normalizedAssignedAgentId)) {
      return false;
    }
  }

  // 7–8. Etiquetas (incluir / excluir): mismos arreglos de tags que en conversación; grupos AND/OR como en TagsInFilter / TagsOutFilter
  const includeTagGroups = normalizeAdvancedFilterTagGroups(filter.advancedFilters?.tags_tag_id_in);
  const excludeTagGroups = normalizeAdvancedFilterTagGroups(filter.advancedFilters?.tags_tag_id_not_in);

  if (includeTagGroups.length > 0 || excludeTagGroups.length > 0) {
    const conversationTagIds = extractConversationTagIds(attrs?.tags);

    if (includeTagGroups.length > 0) {
      const matchesInclude = includeTagGroups.some((group) => group.every((tagId) => (
        conversationTagIds.includes(tagId)
      )));
      if (!matchesInclude) {
        return false;
      }
    }

    if (excludeTagGroups.length > 0) {
      const matchesExcludeGroup = excludeTagGroups.some((group) => group.every((tagId) => (
        conversationTagIds.includes(tagId)
      )));
      if (matchesExcludeGroup) {
        return false;
      }
    }
  }

  // 9. Validar temperatura del lead (basado en score)
  if (filter.advancedFilters?.lead_temperature) {
    const score = attrs.lead_score;
    if (score == null) {
      return false;
    }
    const level = getTemperatureLevelFromScore(score);
    if (level !== filter.advancedFilters.lead_temperature) {
      return false;
    }
  }

  // 10. Validar asignación según filtro de conversación
  switch (filter.conversation) {
    case 'inbox':
      if (normalizedAssignedAgentId !== normalizedCurrentUserId) {
        return false;
      }
      break;

    case 'not_assigned':
      if (assignedAgentId !== null) {
        return false;
      }
      break;

    case 'all':
    default:
      break;
  }

  // Si pasó todas las validaciones
  return true;
}
